import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import type { TextOperationMeta } from './operationCatalog';
import { isSignInRequired } from '../../../components/auth/signInRequiredError';

interface Props {
  meta: TextOperationMeta;
  /** Shown when the op needs an existing draft but none is active. */
  needsDocument?: boolean;
  validationError?: string | null;
  mutationError?: Error | null;
  busy: boolean;
  starting?: boolean;
  submitDisabled: boolean;
  submitLabel: string;
  busyLabel: string;
  onSubmit: () => void;
  children?: React.ReactNode;
  /** Extra content under the button (e.g. Critique suggestions). */
  footer?: React.ReactNode;
}

/**
 * Shared chrome for the text-operation panels: header (icon + label + blurb),
 * the op's form fields, validation/error alerts, and the submit button. Keeps
 * the 8 panels thin and visually consistent (parallels Image Lab's per-panel
 * layout without re-typing it eight times).
 */
export const PanelScaffold: React.FC<Props> = ({
  meta,
  needsDocument = false,
  validationError,
  mutationError,
  busy,
  starting = false,
  submitDisabled,
  submitLabel,
  busyLabel,
  onSubmit,
  children,
  footer,
}) => {
  const Icon = meta.icon;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon fontSize="small" />
        <Typography variant="subtitle2">{meta.label}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {meta.description}
      </Typography>

      {needsDocument && (
        <Alert severity="info">Generate a draft first — this operation needs an existing document.</Alert>
      )}

      {children}

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutationError && !isSignInRequired(mutationError) && (
        <Alert severity="error">
          {mutationError instanceof Error ? mutationError.message : 'Operation failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitDisabled}
          startIcon={busy || starting ? <CircularProgress size={16} /> : <Icon />}
        >
          {starting ? 'Starting session…' : busy ? busyLabel : submitLabel}
        </Button>
      </Box>

      {footer}
    </Stack>
  );
};
